import { apiConfig } from '@/config/env';
import type {
  ActivationRequest,
  ActivationResponse
} from '@/types/activation';
import type {
  PlaylistUploadRequest,
  PlaylistUploadResponse
} from '@/types/playlist';
import type {
  ResellerDashboardData,
  ResellerClient,
  CreateClientRequest,
  CreateClientResponse,
  ClientFilters,
  ActivationCode,
  ResellerEarning
} from '@/types/reseller';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Generic API client
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = apiConfig.baseUrl) {
    this.baseUrl = `${baseUrl}/${apiConfig.version}`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData.code
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network or other errors
      throw new ApiError(
        'Erro de conexão. Verifique sua internet e tente novamente.',
        0
      );
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    
    return this.request<T>(endpoint + url.search, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Create API client instance
const apiClient = new ApiClient();

// Activation API
export const activationApi = {
  async activate(request: ActivationRequest): Promise<ActivationResponse> {
    return apiClient.post<ActivationResponse>('/activation/activate', request);
  },

  async validateCode(code: string): Promise<{ valid: boolean; details?: any }> {
    return apiClient.get<{ valid: boolean; details?: any }>(`/activation/validate/${code}`);
  },

  async getActivationHistory(userId: string): Promise<any[]> {
    return apiClient.get<any[]>(`/activation/history/${userId}`);
  }
};

// Playlist API
export const playlistApi = {
  async webUpload(data: {
    mac: string;
    name: string;
    url: string;
    epgUrl?: string;
    pin?: string;
    isProtected?: boolean;
    isSelected?: boolean;
  }): Promise<any> {
    return apiClient.post<any>('/playlists/web-upload', data);
  },

  async upload(request: PlaylistUploadRequest): Promise<PlaylistUploadResponse> {
    return apiClient.post<PlaylistUploadResponse>('/playlist/upload', request);
  },

  async getPlaylists(userId: string): Promise<any[]> {
    return apiClient.get<any[]>(`/playlist/user/${userId}`);
  },

  async deletePlaylist(playlistId: string): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(`/playlist/${playlistId}`);
  },

  async validatePlaylist(file: File): Promise<{ valid: boolean; channels?: number; errors?: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    
    // For file uploads, we need to handle differently
    const response = await fetch(`${apiConfig.baseUrl}/${apiConfig.version}/playlist/validate`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Erro ao validar playlist', response.status);
    }

    return response.json();
  }
};

// Reseller API
export const resellerApi = {
  async register(data: {
    name: string;
    surname: string;
    email: string;
    password: string;
    phone?: string;
    company?: string;
    country?: string;
    partnerType?: 'reseller' | 'referral';
  }): Promise<any> {
    return apiClient.post<any>('/resellers/register', data);
  },

  async getDashboard(): Promise<ResellerDashboardData> {
    return apiClient.get<ResellerDashboardData>('/reseller/dashboard');
  },

  async getClients(filters?: ClientFilters): Promise<ResellerClient[]> {
    return apiClient.get<ResellerClient[]>('/reseller/clients', filters);
  },

  async createClient(request: CreateClientRequest): Promise<CreateClientResponse> {
    return apiClient.post<CreateClientResponse>('/reseller/clients', request);
  },

  async updateClient(clientId: string, data: Partial<ResellerClient>): Promise<ResellerClient> {
    return apiClient.patch<ResellerClient>(`/reseller/clients/${clientId}`, data);
  },

  async deleteClient(clientId: string): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(`/reseller/clients/${clientId}`);
  },

  async generateCodes(planId: string, quantity: number): Promise<ActivationCode[]> {
    return apiClient.post<ActivationCode[]>('/reseller/codes/generate', {
      planId,
      quantity
    });
  },

  async getCodes(status?: string): Promise<ActivationCode[]> {
    return apiClient.get<ActivationCode[]>('/reseller/codes', { status });
  },

  async getEarnings(period?: string): Promise<ResellerEarning[]> {
    return apiClient.get<ResellerEarning[]>('/reseller/earnings', { period });
  },

  async getStats(): Promise<any> {
    return apiClient.get<any>('/reseller/stats');
  }
};

// User API
export const userApi = {
  async getProfile(): Promise<any> {
    return apiClient.get<any>('/user/profile');
  },

  async updateProfile(data: any): Promise<any> {
    return apiClient.patch<any>('/user/profile', data);
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>('/user/change-password', {
      currentPassword,
      newPassword
    });
  }
};

// Auth API (for password reset, etc.)
export const authApi = {
  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/auth/password-reset/request', {
      email
    });
  },

  async resetPassword(code: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/auth/password-reset/confirm', {
      code,
      newPassword
    });
  },

  async resendVerificationCode(email: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/auth/resend-verification', {
      email
    });
  }
};

// Packages API
export const packagesApi = {
  async getWebPackages(): Promise<any[]> {
    // Backend wraps: { success, data: { error, message: { count, rows } } }
    const res: any = await apiClient.get<any>('/packages/web');
    if (Array.isArray(res)) return res;
    return res?.data?.message?.rows ?? res?.message?.rows ?? res?.data?.rows ?? [];
  },
};

// Billing API
export type CheckoutMethod = 'pix' | 'credit_card';
export interface CheckoutResponse {
  transactionId: string;
  method: CheckoutMethod;
  provider: string;
  status: string;
  amount: number;
  currency: string;
  paymentUrl?: string;
  pix?: { qrCodeBase64?: string; copyPaste?: string; expiresAt?: string };
}
// The backend wraps most responses, while status/confirm may be unwrapped.
function billingData<T>(value: T | { data: T }): T {
  return value && typeof value === 'object' && 'data' in value ? value.data : value as T;
}
export const billingApi = {
  async getMethods(): Promise<{ method: CheckoutMethod; provider: string }[]> {
    return billingData(await apiClient.get('/billing/methods'));
  },
  async getCheckoutStatus(id: string): Promise<{ transactionId: string; status: string; expiresAt?: string; paymentUrl?: string }> {
    return billingData(await apiClient.get(`/billing/payment/${encodeURIComponent(id)}/status`));
  },
  async createActivationPayment(data: {
    mac: string;
    packageId: number;
    email?: string;
    successUrl: string;
    cancelUrl: string;
    lang?: string;
    paymentMethod?: CheckoutMethod;
  }): Promise<CheckoutResponse> {
    return billingData(await apiClient.post<CheckoutResponse | { data: CheckoutResponse }>(
      '/billing/payment/activation',
      data
    ));
  },

  async confirmPayment(transactionId: string): Promise<{ success: boolean; status: string; device?: any }> {
    return apiClient.post<{ success: boolean; status: string; device?: any }>(
      '/billing/payment/confirm',
      { transactionId }
    );
  },
};

// Devices API
export const devicesApi = {
  async checkMacStatus(mac: string): Promise<{ error: boolean; device?: any; message?: string }> {
    return apiClient.get<{ error: boolean; device?: any; message?: string }>('/mac/status', { mac });
  },
};

// Export the API client for custom requests
export { apiClient, ApiError };

// Export all APIs
export const api = {
  activation: activationApi,
  playlist: playlistApi,
  reseller: resellerApi,
  user: userApi,
  auth: authApi,
  packages: packagesApi,
  billing: billingApi,
  devices: devicesApi,
};
